"use client";

import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { usePathname } from "next/navigation";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { RenderIcon } from "@/components/render-icon";
import { MarkStack, NAV_MARK_APPS } from "@/components/settings/connected-apps/agent-apps";
import { useHasRootPriviliges } from "@/hooks/auth/use-has-root-priviliges";
import { settingsNavItems } from "./settings-nav-config";
import { GuardedLink } from "../navigation/guarded-link";

export function SettingsSidebar() {
  const pathname = usePathname();
  const { hasRootPriviliges } = useHasRootPriviliges();
  const { setOpenMobile } = useSidebar();

  const items = React.useMemo(() => {
    return hasRootPriviliges
      ? settingsNavItems
      : settingsNavItems.filter((item) => item.id === "profile");
  }, [hasRootPriviliges]);

  if (!pathname.includes("/p/settings")) {
    return null;
  }

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <GuardedLink href="/">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-sidebar-accent">
                  <ArrowLeft className="h-4 w-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Back to Pylo</span>
                  <span className="truncate text-xs text-muted-foreground">Settings</span>
                </div>
              </GuardedLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton tooltip={item.title} asChild isActive={isActive}>
                    <GuardedLink
                      href={item.href}
                      className="text-muted-foreground"
                      onClick={() => setOpenMobile(false)}
                    >
                      <RenderIcon
                        iconName={item.icon || "FileSpreadsheet"}
                        size={14}
                        className="text-muted-foreground"
                      />
                      <div className="w-full overflow-hidden">{item.title}</div>
                      {item.brandMarks && <MarkStack apps={NAV_MARK_APPS} size="sm" />}
                    </GuardedLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
